import { useState, useEffect } from "react";
import { z } from "zod";
import { useDateRange } from "../context/DateRangeContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Zod validation schema
const callLogSchema = z.object({
  recordingLink: z.string().url("Please enter a valid URL"),
  callType: z.string().optional(),
  disposition: z.string().min(1, "Please select a disposition"),
  notes: z.string().optional(),
});

type CallLogFormData = z.infer<typeof callLogSchema>;

// Types
interface CallLog {
  _id: string;
  recordingLink: string;
  callType?: string;
  disposition: string;
  notes?: string;
  createdAt: string;
  userId: string;
}

interface FormErrors {
  recordingLink?: string;
  disposition?: string;
}

// Options
const CALL_TYPES = [
  { value: "", label: "Select type (optional)" },
  { value: "Discovery Call", label: "Discovery Call" },
  { value: "Close Call", label: "Close Call" },
  { value: "Follow Up", label: "Follow Up" },
  { value: "Check-in", label: "Check-in" },
];

const DISPOSITIONS = [
  { value: "", label: "Select outcome" },
  { value: "Sale Closed", label: "Sale Closed" },
  { value: "Follow Up Scheduled", label: "Follow Up Scheduled" },
  { value: "Not Interested", label: "Not Interested" },
  { value: "Wrong Number / Bad Lead", label: "Wrong Number / Bad Lead" },
  { value: "Rescheduled", label: "Rescheduled" },
];

const REPS = [
  { value: "all", label: "All Reps" },
  { value: "me", label: "My Calls" },
];

// Icons
const Icons = {
  Plus: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Video: () => (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
  Filter: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  ),
  Calendar: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Link: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  ExternalLink: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  ),
  Edit: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Trash: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  Spinner: () => (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  ),
};

export function CallLibrary() {
  const { dateRange, formatForDisplay } = useDateRange();
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [selectedRep, setSelectedRep] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Form state
  const [recordingLink, setRecordingLink] = useState("");
  const [callType, setCallType] = useState("");
  const [disposition, setDisposition] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Edit dialog state
  const [editingCall, setEditingCall] = useState<CallLog | null>(null);
  const [editRecordingLink, setEditRecordingLink] = useState("");
  const [editCallType, setEditCallType] = useState("");
  const [editDisposition, setEditDisposition] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editErrors, setEditErrors] = useState<FormErrors>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete dialog state
  const [deletingCall, setDeletingCall] = useState<CallLog | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch calls on mount and when date range changes
  useEffect(() => {
    fetchCalls();
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchCalls = async () => {
    try {
      setIsLoading(true);
      setFetchError(null);

      const startDate = dateRange.startDate.toISOString().split("T")[0];
      const endDate = dateRange.endDate.toISOString().split("T")[0];

      const response = await fetch(
        `/api/calls?startDate=${startDate}&endDate=${endDate}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch calls");
      }

      setCalls(data.data || []);
    } catch (error) {
      console.error("Error fetching calls:", error);
      setFetchError(error instanceof Error ? error.message : "Failed to fetch calls");
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (data: CallLogFormData, setFormErrors: (e: FormErrors) => void): boolean => {
    const result = callLogSchema.safeParse(data);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setFormErrors({
        recordingLink: fieldErrors.recordingLink?.[0],
        disposition: fieldErrors.disposition?.[0],
      });
      return false;
    }

    setFormErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const formData: CallLogFormData = {
      recordingLink,
      callType: callType || undefined,
      disposition,
      notes: notes || undefined,
    };

    if (!validateForm(formData, setErrors)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.details) {
          setErrors({
            recordingLink: data.details.recordingLink?.[0],
            disposition: data.details.disposition?.[0],
          });
        }
        throw new Error(data.error || "Failed to log call");
      }

      setCalls(prev => [data.data, ...prev]);
      setRecordingLink("");
      setCallType("");
      setDisposition("");
      setNotes("");
      setErrors({});
    } catch (error) {
      console.error("Error logging call:", error);
      setSubmitError(error instanceof Error ? error.message : "Failed to log call");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field: keyof FormErrors, value: string, setter: (v: string) => void) => {
    setter(value);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Edit handlers
  const openEditDialog = (call: CallLog) => {
    setEditingCall(call);
    setEditRecordingLink(call.recordingLink);
    setEditCallType(call.callType || "");
    setEditDisposition(call.disposition);
    setEditNotes(call.notes || "");
    setEditErrors({});
  };

  const closeEditDialog = () => {
    setEditingCall(null);
    setEditErrors({});
  };

  const handleUpdate = async () => {
    if (!editingCall) return;

    const formData: CallLogFormData = {
      recordingLink: editRecordingLink,
      callType: editCallType || undefined,
      disposition: editDisposition,
      notes: editNotes || undefined,
    };

    if (!validateForm(formData, setEditErrors)) {
      return;
    }

    setIsUpdating(true);

    try {
      const response = await fetch(`/api/calls/${editingCall._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.details) {
          setEditErrors({
            recordingLink: data.details.recordingLink?.[0],
            disposition: data.details.disposition?.[0],
          });
        }
        throw new Error(data.error || "Failed to update call");
      }

      setCalls(prev => prev.map(c => c._id === editingCall._id ? data.data : c));
      closeEditDialog();
    } catch (error) {
      console.error("Error updating call:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete handlers
  const handleDelete = async () => {
    if (!deletingCall) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`/api/calls/${deletingCall._id}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete call");
      }

      setCalls(prev => prev.filter(c => c._id !== deletingCall._id));
      setDeletingCall(null);
    } catch (error) {
      console.error("Error deleting call:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getDispositionStyle = (disp: string) => {
    switch (disp) {
      case "Sale Closed":
        return "bg-emerald-50 text-emerald-600";
      case "Not Interested":
      case "Wrong Number / Bad Lead":
        return "bg-rose-50 text-rose-600";
      default:
        return "bg-amber-50 text-amber-600";
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="opacity-0 animate-fade-in-up">
        <h2 className="text-2xl font-bold mb-2">Call Library</h2>
        <p className="text-muted-foreground">Log your calls after each conversation for tracking and review</p>
      </div>

      {/* Log a Call Form */}
      <div className="bg-card rounded-2xl p-6 shadow-brand opacity-0 animate-fade-in-up stagger-1">
        <div className="flex items-center gap-2 mb-2">
          <Icons.Plus />
          <h3 className="text-lg font-semibold">Log a Call</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Takes about 30 seconds - fill this out after every call</p>

        {submitError && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 text-rose-600 text-sm">
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Recording Link <span className="text-rose-500">*</span>
                </label>
                <input
                  type="url"
                  value={recordingLink}
                  onChange={(e) => handleFieldChange("recordingLink", e.target.value, setRecordingLink)}
                  placeholder="https://..."
                  className={`w-full px-4 py-2.5 rounded-xl bg-secondary border text-sm focus:outline-none focus:ring-2 transition-all ${
                    errors.recordingLink
                      ? "border-rose-500 focus:ring-rose-500/20 focus:border-rose-500"
                      : "border-border focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50"
                  }`}
                />
                {errors.recordingLink && (
                  <p className="text-sm text-rose-500">{errors.recordingLink}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Call Type</label>
                <select
                  value={callType}
                  onChange={(e) => setCallType(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50 transition-all appearance-none cursor-pointer"
                >
                  {CALL_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Disposition <span className="text-rose-500">*</span>
                </label>
                <select
                  value={disposition}
                  onChange={(e) => handleFieldChange("disposition", e.target.value, setDisposition)}
                  className={`w-full px-4 py-2.5 rounded-xl bg-secondary border text-sm focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${
                    errors.disposition
                      ? "border-rose-500 focus:ring-rose-500/20 focus:border-rose-500"
                      : "border-border focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50"
                  }`}
                >
                  {DISPOSITIONS.map((disp) => (
                    <option key={disp.value} value={disp.value}>{disp.label}</option>
                  ))}
                </select>
                {errors.disposition && (
                  <p className="text-sm text-rose-500">{errors.disposition}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Quick Notes / Objections</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes about the call..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50 transition-all resize-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white text-sm font-medium hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? <Icons.Spinner /> : <Icons.Link />}
              {isSubmitting ? "Logging..." : "Log Call"}
            </button>
          </div>
        </form>
      </div>

      {/* Call History */}
      <div className="bg-card rounded-2xl p-6 shadow-brand opacity-0 animate-fade-in-up stagger-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold">Call History</h3>
            <p className="text-sm text-muted-foreground">
              All logged calls ({formatForDisplay()})
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Icons.Filter />
              <select
                value={selectedRep}
                onChange={(e) => setSelectedRep(e.target.value)}
                className="px-3 py-2 rounded-lg bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50 transition-all appearance-none cursor-pointer"
              >
                {REPS.map((rep) => (
                  <option key={rep.value} value={rep.value}>{rep.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icons.Calendar />
              <span>{formatForDisplay()}</span>
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
            <p className="mt-4 text-sm text-muted-foreground">Loading calls...</p>
          </div>
        )}

        {!isLoading && fetchError && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-rose-500 mb-2">{fetchError}</p>
            <button onClick={fetchCalls} className="text-sm text-[var(--brand-primary)] hover:underline">
              Try again
            </button>
          </div>
        )}

        {!isLoading && !fetchError && calls.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-muted-foreground/50 mb-4">
              <Icons.Video />
            </div>
            <p className="font-medium text-muted-foreground">No calls logged yet</p>
            <p className="text-sm text-muted-foreground/70">Start logging your calls above</p>
          </div>
        )}

        {!isLoading && !fetchError && calls.length > 0 && (
          <div className="space-y-3">
            {calls.map((call) => (
              <div
                key={call._id}
                className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] flex items-center justify-center text-white">
                  <Icons.Link />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium truncate">{call.callType || "Call"}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDispositionStyle(call.disposition)}`}>
                      {call.disposition}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {call.notes || "No notes"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditDialog(call)}
                    className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-background transition-all text-muted-foreground hover:text-[var(--brand-primary)]"
                    title="Edit call"
                  >
                    <Icons.Edit />
                  </button>
                  <button
                    onClick={() => setDeletingCall(call)}
                    className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-background transition-all text-muted-foreground hover:text-rose-500"
                    title="Delete call"
                  >
                    <Icons.Trash />
                  </button>
                  <a
                    href={call.recordingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-background transition-colors text-muted-foreground hover:text-[var(--brand-primary)]"
                    title="Open recording"
                  >
                    <Icons.ExternalLink />
                  </a>
                  <div className="text-right text-sm text-muted-foreground whitespace-nowrap ml-2">
                    {formatDate(call.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingCall} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Call</DialogTitle>
            <DialogDescription>
              Make changes to your call log entry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Recording Link <span className="text-rose-500">*</span>
              </label>
              <input
                type="url"
                value={editRecordingLink}
                onChange={(e) => {
                  setEditRecordingLink(e.target.value);
                  if (editErrors.recordingLink) {
                    setEditErrors(prev => ({ ...prev, recordingLink: undefined }));
                  }
                }}
                placeholder="https://..."
                className={`w-full px-4 py-2.5 rounded-xl bg-secondary border text-sm focus:outline-none focus:ring-2 transition-all ${
                  editErrors.recordingLink
                    ? "border-rose-500 focus:ring-rose-500/20 focus:border-rose-500"
                    : "border-border focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50"
                }`}
              />
              {editErrors.recordingLink && (
                <p className="text-sm text-rose-500">{editErrors.recordingLink}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Call Type</label>
              <select
                value={editCallType}
                onChange={(e) => setEditCallType(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50 transition-all appearance-none cursor-pointer"
              >
                {CALL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Disposition <span className="text-rose-500">*</span>
              </label>
              <select
                value={editDisposition}
                onChange={(e) => {
                  setEditDisposition(e.target.value);
                  if (editErrors.disposition) {
                    setEditErrors(prev => ({ ...prev, disposition: undefined }));
                  }
                }}
                className={`w-full px-4 py-2.5 rounded-xl bg-secondary border text-sm focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${
                  editErrors.disposition
                    ? "border-rose-500 focus:ring-rose-500/20 focus:border-rose-500"
                    : "border-border focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50"
                }`}
              >
                {DISPOSITIONS.map((disp) => (
                  <option key={disp.value} value={disp.value}>{disp.label}</option>
                ))}
              </select>
              {editErrors.disposition && (
                <p className="text-sm text-rose-500">{editErrors.disposition}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Quick Notes / Objections</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional notes about the call..."
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]/50 transition-all resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isUpdating}>
              {isUpdating ? <Icons.Spinner /> : null}
              {isUpdating ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingCall} onOpenChange={(open) => !open && setDeletingCall(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete call log?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this call log entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDeleting ? <Icons.Spinner /> : null}
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
